import { HOURS } from "./window";

/**
 * V2 SHEPHERD (design section 0, "Scheduling"). The shape of one scheduled agent
 * task, shared by the items that own a schedule (V1 stage_watch, V2 pipeline.sweep,
 * V4 review.cycle, V5 campaign.plan). Each exports its own spec constant from its
 * handler file; `apps/worker/src/agentSchedule.ts` turns a list of them into the
 * tasks that are due. Pure types and clock arithmetic, no IO.
 */
export interface AgentScheduleSpec {
  /** A live task kind (packages/db/src/taskKinds.ts). */
  readonly kind: string;
  /**
   * The task's idempotency key for the period `now` falls in. Two calls inside
   * one period must return the same key, so the queue's (client_id,
   * idempotency_key) conflict makes the second a no-op.
   */
  readonly idempotencyKeyFor: (now: Date, clientId: string) => string;
  /** When the period `now` falls in becomes due. The spec is due once `now` is at or past this. */
  readonly dueAt: (now: Date) => Date;
  readonly payload: Readonly<Record<string, unknown>>;
  /**
   * W2 LEARNER. Optional: tasks sharing a key never run concurrently (the queue's
   * serialize lock). A string is used as is; a function gets the client id
   * (the learn family uses `learn:<client>`). Omitted means no serialization.
   */
  readonly serializeKey?: string | ((clientId: string) => string);
  /**
   * W2 LEARNER. Optional per-run spend envelope in micro-USD, passed to the queue
   * as the task's own max-spend. Omitted means no envelope (the client's daily
   * cap alone), which is visibly different from an envelope of zero.
   */
  readonly budgetMicroUsd?: number;
}

/** `YYYY-MM-DD`, the UTC day. */
export const utcDayKey = (now: Date): string => now.toISOString().slice(0, 10);

/** Today's `hour`:00 UTC. */
export function utcDayAtHour(now: Date, hour: number): Date {
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) throw new Error(`utcDayAtHour: hour must be an integer 0 to 23, got ${hour}`);
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) + hour * HOURS);
}
